import { Field, useFormikContext } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { ListQuery } from '../../models/ListQuery';
import { getBuildingNamesWithPagination } from '../../services/buildingService';
import { getFloorsNamesWithPagination } from '../../services/floorService';
import { getZoneById, getZoneNamesWithPagination } from '../../services/zoneService';
import { IAppState } from '../../store/types';
import { appendFilter } from '../../utilities/filters';
import { validateIntegerWithinBounds } from '../../utilities/Validation';
import Label from '../label/label';
import PaginatedSearchableSelectField from '../selection-field/paginated-searchable-select-field';

/**
* Door(location) doesn't have a direct relationship with building and floor so we get those from door readers(terminals)
* If a door doesn't have readers assigned to it we then disable building and floor field.
* Otherwise we get building and floor to any assigned reader.
* Saving building and/or floor should affect all assigned readers.
*/
export const DoorDetails: FC = () => {
    const { translate } = useLocale();
    const { errors, values, setFieldValue } = useFormikContext<any>();
    const globalSiteId = useSelector((state: IAppState) => state.site.site.value)
    const [siteId, setSiteId] = useState<any>(0);

    /**
    * Building needs to be filter so that it belong to the same site as the selected hardware,
    * so we are pulling site from selected zone.
    */
    useEffect(() => {
        try {
            (async () => {
                if (values.zoneId && values.zoneId !== 0) {
                    const zone = await getZoneById(values.zoneId, translate);
                    setSiteId(zone.siteId);
                } else {
                    setSiteId(values.siteId ?? globalSiteId)
                }
            })();
        } catch (error) {
            setSiteId(values.siteId ?? globalSiteId)
        }
    }, [values.zoneId])

    return <>
        <div className="flex flex-row flex-1 pt-3 ">
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='buildingId'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.building)}:</Label>
                <PaginatedSearchableSelectField
                    name={translate.byKey(TranslationKeys.building)}
                    key={"buildingId"}
                    accessor='buildingId'
                    dataMethod={(listParameters?: ListQuery) => getBuildingNamesWithPagination({ ...listParameters, filters: appendFilter(siteId, 'Site id', 'site.id', []) })}
                    searchAccessor={'buildingName'}
                    onSelect={(_,item) => {
                        setFieldValue('building', item?.label)
                        setFieldValue('buildingId', item?.value)
                     }}
                    selectedItemLabel={values.building}
                    disabled={!!!values.exitReaderId && !!!values.entryReaderId}
                    dependent={{ accessor: 'zoneId', name: 'Zone', key: 'site.id', value: siteId, childAcceccor: 'buildingId' }} //Monitor zone change but filter by site id
                    onDelete={(event) => { setFieldValue('buildingId', undefined); setFieldValue('floorId', undefined) }} />
            </div>
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='floorId'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.floor)}:</Label>
                <PaginatedSearchableSelectField
                    name={translate.byKey(TranslationKeys.floor)}
                    key={"floorId"}
                    accessor='floorId'
                    dataMethod={getFloorsNamesWithPagination}
                    searchAccessor={'floorName'}
                    onSelect={(_,item) => {
                        setFieldValue('floor', item?.label)
                        setFieldValue('floorId', item?.value)
                     }}
                    selectedItemLabel={values.floor}
                    onDelete={(event) => setFieldValue('floorId', undefined)}
                    dependent={{ accessor: 'buildingId', name: 'Building', key: 'building.id', value: values.buildingId, childAcceccor: 'floorId' }}
                    disabled={!!!values.buildingId} />
            </div>
        </div>
        <div className="flex flex-row flex-1 pt-3 ">
            <div className="flex flex-col flex-1 pr-3 ">
                <Label htmlFor='interLeadingZone'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.interLeadingZone)}:</Label>
                <PaginatedSearchableSelectField
                    name={translate.byKey(TranslationKeys.interLeadingZone)}
                    key={"interleadingZoneSelectField"}
                    accessor='zoneByInterleadingZoneId'
                    dataMethod={getZoneNamesWithPagination}
                    searchAccessor={'zoneName'}
                    onSelect={(_,item) => {
                        setFieldValue('zoneByInterleadingZoneName', item?.label)
                        setFieldValue('zoneByInterleadingZoneId', item?.value)
                     }}
                    onDelete={(event) => setFieldValue('zoneByInterleadingZoneId', undefined)}
                    selectedItemLabel={values.zoneByInterleadingZoneName} />
            </div>
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='multiTag'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.multiTag)}:</Label>
                <Field
                    name='multiTag'
                    type='number'
                    min={0} max={5}
                    validate={(value) => validateIntegerWithinBounds(value, 0, 5, translate)}
                    className={'flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input'} />
                {errors.multiTag ? <span className='text-red-600 text-xs'>{errors.multiTag}</span> : null}
            </div>
        </div>
    </>
};

